import { useState, useCallback, useMemo } from 'react'
import type {
  DiscoveryState,
  DiscoveryStep,
  UseCaseId,
  MissionProfile,
  ChallengeAssessment,
} from '@/lib/discovery-data'
import { initialDiscoveryState, discoverySteps } from '@/lib/discovery-data'
import {
  calculateValueMetrics,
  buildRecommendedSolution,
  buildQuoteFromSolution,
  calculateTotalInvestment,
  type ValueMetrics,
} from '@/lib/value-calculator'
import type { QuoteItem } from '@/lib/pricing-data'

export interface UseDiscoveryReturn {
  // State
  state: DiscoveryState
  currentStepIndex: number
  totalSteps: number
  progress: number // 0-100
  isFirstStep: boolean
  isLastStep: boolean

  // Navigation
  goToStep: (step: DiscoveryStep) => void
  nextStep: () => void
  prevStep: () => void
  canProgress: boolean

  // State updates
  setUseCase: (useCase: UseCaseId) => void
  updateMissionProfile: (updates: Partial<MissionProfile>) => void
  updateChallenges: (updates: Partial<ChallengeAssessment>) => void
  setCustomerInfo: (info: { name?: string; company?: string; email?: string }) => void

  // Computed values
  valueMetrics: ValueMetrics | null
  recommendedSolution: ReturnType<typeof buildRecommendedSolution> | null
  quoteItems: QuoteItem[]
  totalInvestment: number

  // Quote customization
  addQuoteItem: (item: QuoteItem) => void
  removeQuoteItem: (itemId: string) => void
  updateQuoteItemQuantity: (itemId: string, quantity: number) => void

  // Reset
  reset: () => void
}

export function useDiscovery(): UseDiscoveryReturn {
  const [state, setState] = useState<DiscoveryState>(initialDiscoveryState)
  const [customQuoteItems, setCustomQuoteItems] = useState<QuoteItem[]>([])

  // Step navigation helpers
  const currentStepIndex = discoverySteps.findIndex((s) => s.id === state.currentStep)
  const totalSteps = discoverySteps.length
  const progress = ((currentStepIndex + 1) / totalSteps) * 100
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === totalSteps - 1

  // Validation for step progression
  const canProgress = useMemo(() => {
    switch (state.currentStep) {
      case 'use-case':
        return state.useCase !== null
      case 'mission-profile':
        return state.missionProfile.fleetSize > 0 && state.missionProfile.monthlyFlightHours > 0
      case 'challenge':
        return state.challenges.annualMissions > 0 && state.challenges.costPerFailedMission > 0
      case 'value':
        return true // Always can proceed from value
      case 'solution':
        return true // Always can proceed from solution
      case 'summary':
        return state.customerName.trim().length > 0
      default:
        return true
    }
  }, [state])

  // Navigation
  const goToStep = useCallback((step: DiscoveryStep) => {
    setState((prev) => ({ ...prev, currentStep: step }))
  }, [])

  const nextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1
    if (nextIndex < totalSteps) {
      setState((prev) => ({ ...prev, currentStep: discoverySteps[nextIndex].id }))
    }
  }, [currentStepIndex, totalSteps])

  const prevStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      setState((prev) => ({ ...prev, currentStep: discoverySteps[prevIndex].id }))
    }
  }, [currentStepIndex])

  // State updates
  const setUseCase = useCallback((useCase: UseCaseId) => {
    setState((prev) => ({ ...prev, useCase }))
  }, [])

  const updateMissionProfile = useCallback((updates: Partial<MissionProfile>) => {
    setState((prev) => ({
      ...prev,
      missionProfile: { ...prev.missionProfile, ...updates },
    }))
  }, [])

  const updateChallenges = useCallback((updates: Partial<ChallengeAssessment>) => {
    setState((prev) => ({
      ...prev,
      challenges: { ...prev.challenges, ...updates },
    }))
  }, [])

  const setCustomerInfo = useCallback(
    (info: { name?: string; company?: string; email?: string }) => {
      setState((prev) => ({
        ...prev,
        customerName: info.name ?? prev.customerName,
        customerCompany: info.company ?? prev.customerCompany,
        customerEmail: info.email ?? prev.customerEmail,
      }))
    },
    []
  )

  // Computed: Recommended solution
  const recommendedSolution = useMemo(() => {
    if (!state.useCase) return null
    return buildRecommendedSolution(state.useCase, state.missionProfile)
  }, [state.useCase, state.missionProfile])

  // Computed: Quote items (recommended + custom)
  const quoteItems = useMemo(() => {
    if (!recommendedSolution) return customQuoteItems

    const recommended = buildQuoteFromSolution(recommendedSolution)
    
    // Merge: custom items override recommended
    const customIds = new Set(customQuoteItems.map((qi) => qi.item.id))
    const baseItems = recommended.filter((qi) => !customIds.has(qi.item.id))
    
    return [...baseItems, ...customQuoteItems]
  }, [recommendedSolution, customQuoteItems])

  // Computed: Total investment
  const totalInvestment = useMemo(() => {
    return calculateTotalInvestment(quoteItems)
  }, [quoteItems])

  // Computed: Value metrics
  const valueMetrics = useMemo(() => {
    if (!state.useCase) return null
    return calculateValueMetrics(
      state.useCase,
      state.missionProfile,
      state.challenges,
      totalInvestment
    )
  }, [state.useCase, state.missionProfile, state.challenges, totalInvestment])

  // Quote customization
  const addQuoteItem = useCallback((item: QuoteItem) => {
    setCustomQuoteItems((prev) => {
      const existing = prev.find((qi) => qi.item.id === item.item.id)
      if (existing) {
        return prev.map((qi) =>
          qi.item.id === item.item.id
            ? { ...qi, quantity: qi.quantity + item.quantity }
            : qi
        )
      }
      return [...prev, item]
    })
  }, [])

  const removeQuoteItem = useCallback((itemId: string) => {
    setCustomQuoteItems((prev) => prev.filter((qi) => qi.item.id !== itemId))
  }, [])

  const updateQuoteItemQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity <= 0) {
      setCustomQuoteItems((prev) => prev.filter((qi) => qi.item.id !== itemId))
    } else {
      setCustomQuoteItems((prev) =>
        prev.map((qi) => (qi.item.id === itemId ? { ...qi, quantity } : qi))
      )
    }
  }, [])

  // Reset
  const reset = useCallback(() => {
    setState(initialDiscoveryState)
    setCustomQuoteItems([])
  }, [])

  return {
    state,
    currentStepIndex,
    totalSteps,
    progress,
    isFirstStep,
    isLastStep,
    goToStep,
    nextStep,
    prevStep,
    canProgress,
    setUseCase,
    updateMissionProfile,
    updateChallenges,
    setCustomerInfo,
    valueMetrics,
    recommendedSolution,
    quoteItems,
    totalInvestment,
    addQuoteItem,
    removeQuoteItem,
    updateQuoteItemQuantity,
    reset,
  }
}

